import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, X, RotateCcw } from "lucide-react";
import { cmdApi } from "../api";
import { usePermissions } from "../hooks/usePermissions";
import { useCmdAuth } from "../contexts/CmdAuthContext";
import { fmtDate, fmtDateTime } from "../format";

const CLOSER_ROLES = ["Founder", "Co-founder", "CFO", "Finance Director"];
const VOTE_OPTS = [
  { key: "approve", label: "Approve" },
  { key: "hold", label: "Hold" },
  { key: "reject", label: "Reject" },
];
const OUTCOME = {
  approved: { label: "Approved", cls: "health-ok" },
  hold: { label: "On hold", cls: "health-watch" },
  rejected: { label: "Rejected", cls: "health-risk" },
};

function Sheet({ onClose, title, children, testId }) {
  return (
    <>
      <div className="cmd-sheet-backdrop" onClick={onClose} />
      <div className="cmd-sheet" role="dialog" aria-modal="true" data-testid={testId}>
        <div className="cmd-sheet-head">
          <h2>{title}</h2>
          <button className="cmd-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="cmd-sheet-body">{children}</div>
      </div>
    </>
  );
}

function voterLine(votes, users) {
  const entries = Object.entries(votes || {});
  if (!entries.length) return null;
  return entries.map(([uid, v]) => {
    const u = users.find((x) => x.id === uid);
    return `${u ? u.name : "Someone"}: ${v}`;
  }).join(" · ");
}

function DecisionCard({ d, users, canVote, canCloseRole, myId, onChanged }) {
  const [closing, setClosing] = useState(null); // outcome key while the note box is open
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const votes = d.votes || {};
  const counts = { approve: 0, hold: 0, reject: 0 };
  for (const v of Object.values(votes)) counts[v] = (counts[v] || 0) + 1;
  const mine = votes[myId] || null;
  const isOpen = d.status === "open";
  const outcome = OUTCOME[d.status];

  const castVote = async (v) => {
    if (!canVote || !isOpen) return;
    setBusy(true);
    try {
      await cmdApi.post(`/decisions/${d.id}/vote`, { vote: v === mine ? null : v });
      onChanged();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Vote failed.");
    } finally { setBusy(false); }
  };

  const closeAs = async (status) => {
    setBusy(true);
    try {
      await cmdApi.post(`/decisions/${d.id}/close`, { status, decision_note: note || null });
      toast.success(`Decision closed as ${OUTCOME[status].label.toLowerCase()}`);
      setClosing(null); setNote("");
      onChanged();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not close.");
    } finally { setBusy(false); }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await cmdApi.post(`/decisions/${d.id}/reopen`);
      toast.success("Decision reopened");
      onChanged();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not reopen.");
    } finally { setBusy(false); }
  };

  return (
    <div className={`cmd-decision ${isOpen ? "" : `decided ${d.status}`}`} data-testid={`decision-${d.id}`}>
      <div className="cmd-decision-head">
        <div>
          <div className="cmd-card-title">{d.title}</div>
          <div className="cmd-card-meta">
            <span className="cmd-card-init">{d.initiative_name}</span>
            <span className="cmd-muted" style={{ fontSize: 12 }}>
              Raised by {d.raised_by === "system" ? "Suggested starting point" : d.raised_by_name} · {fmtDate(d.created_at)}
            </span>
          </div>
        </div>
        {!isOpen && outcome && (
          <span className={`cmd-pill ${outcome.cls}`} data-testid={`outcome-${d.id}`}>{outcome.label}</span>
        )}
      </div>

      {d.context && <p className="cmd-decision-context">{d.context}</p>}

      <div className="cmd-vote-row">
        {VOTE_OPTS.map((o) => (
          <button
            key={o.key}
            className={`cmd-vote-btn v-${o.key} ${mine === o.key ? "mine" : ""}`}
            disabled={!canVote || !isOpen || busy}
            onClick={() => castVote(o.key)}
            data-testid={`vote-${o.key}-${d.id}`}
            title={!isOpen ? "Voting closed" : mine === o.key ? "Click again to withdraw" : `Vote ${o.label.toLowerCase()}`}
          >
            {o.label} <span className="cmd-vote-count">{counts[o.key]}</span>
          </button>
        ))}
      </div>
      {voterLine(votes, users) && (
        <div className="cmd-voters" data-testid={`voters-${d.id}`}>{voterLine(votes, users)}</div>
      )}

      {!isOpen && (
        <div className="cmd-closed-meta" data-testid={`closed-meta-${d.id}`}>
          Closed by {d.decided_by_name} · {fmtDateTime(d.decided_at)}
          {d.decision_note && <div className="cmd-decision-context" style={{ marginTop: 6 }}>{d.decision_note}</div>}
        </div>
      )}

      {isOpen && canCloseRole && (
        <div className="cmd-close-row">
          {closing ? (
            <div className="cmd-close-form">
              <input className="cmd-input" placeholder="Decision note (optional)"
                value={note} onChange={(e) => setNote(e.target.value)} data-testid={`close-note-${d.id}`} />
              <button className="cmd-btn cmd-btn-primary" disabled={busy}
                onClick={() => closeAs(closing)} data-testid={`close-confirm-${d.id}`}>
                {busy ? "Closing…" : `Confirm ${OUTCOME[closing].label.toLowerCase()}`}
              </button>
              <button className="cmd-btn cmd-btn-ghost" onClick={() => { setClosing(null); setNote(""); }}>Cancel</button>
            </div>
          ) : (
            <>
              <button className="cmd-btn cmd-btn-ghost" disabled={busy} onClick={() => setClosing("approved")}
                data-testid={`close-approved-${d.id}`}>Close as approved</button>
              <button className="cmd-btn cmd-btn-ghost" disabled={busy} onClick={() => setClosing("hold")}
                data-testid={`close-hold-${d.id}`}>Close as on hold</button>
              <button className="cmd-btn cmd-btn-ghost" disabled={busy} onClick={() => setClosing("rejected")}
                data-testid={`close-rejected-${d.id}`}>Close as rejected</button>
            </>
          )}
        </div>
      )}
      {!isOpen && canCloseRole && (
        <div className="cmd-close-row">
          <button className="cmd-btn cmd-btn-ghost" disabled={busy} onClick={reopen}
            data-testid={`reopen-${d.id}`}>
            <RotateCcw size={13} style={{ marginRight: 6 }} />Reopen
          </button>
        </div>
      )}
    </div>
  );
}

function RaiseForm({ initiatives, onSaved, onClose }) {
  const [f, setF] = useState({ title: "", initiative_id: "", context: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.initiative_id) { toast.error("Pick an initiative."); return; }
    setBusy(true);
    try {
      await cmdApi.post("/decisions", { ...f, context: f.context || null });
      toast.success("Decision raised");
      onSaved();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not raise decision.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="decision-form">
      <div className="cmd-field">
        <label>Decision (one line)</label>
        <input className="cmd-input" value={f.title} required maxLength={300}
          onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} data-testid="decision-form-title" />
      </div>
      <div className="cmd-field">
        <label>Initiative</label>
        <select className="cmd-input" value={f.initiative_id} required
          onChange={(e) => setF((s) => ({ ...s, initiative_id: e.target.value }))} data-testid="decision-form-initiative">
          <option value="">Pick an initiative…</option>
          {initiatives.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
      </div>
      <div className="cmd-field">
        <label>Context, options, recommendation</label>
        <textarea className="cmd-input" rows={5} value={f.context}
          onChange={(e) => setF((s) => ({ ...s, context: e.target.value }))} data-testid="decision-form-context" />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" className="cmd-btn cmd-btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="cmd-btn cmd-btn-primary" disabled={busy} data-testid="decision-form-save">
          {busy ? "Raising…" : "Raise decision"}
        </button>
      </div>
    </form>
  );
}

export default function CmdDecisions() {
  const { can, role } = usePermissions();
  const { me } = useCmdAuth();
  const canVote = can("decisions", "edit");
  const canCloseRole = canVote && CLOSER_ROLES.includes(role);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [formOpen, setFormOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: dec }, { data: us }, { data: inits }] = await Promise.all([
        cmdApi.get("/decisions"),
        cmdApi.get("/users"),
        cmdApi.get("/initiatives"),
      ]);
      setData(dec);
      setUsers(us.items || []);
      setInitiatives(inits.items || []);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not load decisions.");
      setData({ open: [], decided: [] });
    }
  };
  useEffect(() => { load(); }, []);

  const open = data?.open || [];
  const decided = data?.decided || [];

  return (
    <section data-testid="cmd-decisions">
      <div className="cmd-toolbar" style={{ alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em" }}>Decisions</h1>
        {canVote && (
          <button className="cmd-btn cmd-btn-primary" style={{ marginLeft: "auto" }}
            onClick={() => setFormOpen(true)} data-testid="raise-decision">
            <Plus size={16} style={{ marginRight: 6 }} />Raise decision
          </button>
        )}
      </div>

      {data === null ? (
        <p className="cmd-muted">Loading…</p>
      ) : (
        <>
          <h3 className="cmd-vertical-head" data-testid="waiting-head">Waiting for a call ({open.length})</h3>
          {open.length === 0 && (
            <div className="cmd-empty" style={{ marginBottom: 22 }} data-testid="decisions-empty">
              <h3>Nothing waiting</h3>
              <p>Raise a decision when the group needs to make a call.</p>
            </div>
          )}
          {open.map((d) => (
            <DecisionCard key={d.id} d={d} users={users} canVote={canVote}
              canCloseRole={canCloseRole} myId={me?.user?.id} onChanged={load} />
          ))}

          <h3 className="cmd-vertical-head" style={{ marginTop: 26 }} data-testid="decided-head">Decided ({decided.length})</h3>
          {decided.length === 0 && <p className="cmd-muted" style={{ fontSize: 13 }}>No decisions closed yet.</p>}
          {decided.map((d) => (
            <DecisionCard key={d.id} d={d} users={users} canVote={canVote}
              canCloseRole={canCloseRole} myId={me?.user?.id} onChanged={load} />
          ))}
        </>
      )}

      {formOpen && (
        <Sheet onClose={() => setFormOpen(false)} title="Raise decision" testId="decision-form-sheet">
          <RaiseForm initiatives={initiatives} onSaved={() => { setFormOpen(false); load(); }}
            onClose={() => setFormOpen(false)} />
        </Sheet>
      )}
    </section>
  );
}
